import React, { useState, useEffect } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface InlineDiffProps {
  oldText: string;
  newText: string;
  onOldTextChange?: (text: string) => void;
  onNewTextChange?: (text: string) => void;
}

const InlineDiff: React.FC<InlineDiffProps> = ({
  oldText,
  newText,
  onOldTextChange,
  onNewTextChange,
}) => {
  const [diffLines, setDiffLines] = useState<
    Array<{
      type: "added" | "removed" | "unchanged";
      value: string;
      lineNumber: number;
    }>
  >([]);

  useEffect(() => {
    // Simple diff algorithm to identify added, removed, and unchanged lines
    const oldLines = oldText.split("\n");
    const newLines = newText.split("\n");

    const result: Array<{
      type: "added" | "removed" | "unchanged";
      value: string;
      lineNumber: number;
    }> = [];

    // First, add all removed lines
    oldLines.forEach((line, index) => {
      if (!newLines.includes(line)) {
        result.push({
          type: "removed",
          value: line,
          lineNumber: index + 1,
        });
      }
    });

    // Then, add all lines from the new text
    newLines.forEach((line, index) => {
      if (!oldLines.includes(line)) {
        result.push({
          type: "added",
          value: line,
          lineNumber: index + 1,
        });
      } else {
        result.push({
          type: "unchanged",
          value: line,
          lineNumber: index + 1,
        });
      }
    });

    // Sort by line number
    result.sort((a, b) => a.lineNumber - b.lineNumber);

    setDiffLines(result);
  }, [oldText, newText]);

  // Handle manual edits to the diff
  const handleLineEdit = (index: number, newValue: string) => {
    const updatedDiffLines = [...diffLines];
    updatedDiffLines[index].value = newValue;
    setDiffLines(updatedDiffLines);

    // Reconstruct the text and call the callbacks
    const reconstructedOldText = diffLines
      .filter((line) => line.type !== "added")
      .map((line) => line.value)
      .join("\n");

    const reconstructedNewText = diffLines
      .filter((line) => line.type !== "removed")
      .map((line) => line.value)
      .join("\n");

    if (onOldTextChange) {
      onOldTextChange(reconstructedOldText);
    }

    if (onNewTextChange) {
      onNewTextChange(reconstructedNewText);
    }
  };

  return (
    <div className="flex flex-col w-full">
      <ScrollArea className="h-[400px] w-full p-4 bg-background">
        <div className="space-y-1">
          {diffLines.map((line, index) => (
            <div
              key={index}
              className={cn(
                "py-1 px-2 font-mono text-sm rounded",
                line.type === "added" &&
                  "bg-good-bg-good-bg/30 text-good",
                line.type === "removed" &&
                  "bg-danger-bg-danger-bg/30 text-danger line-through",
                line.type === "unchanged" && "bg-transparent",
              )}
            >
              <div className="flex">
                <span className="w-8 text-muted-foreground text-right pr-2">
                  {line.lineNumber}
                </span>
                <span
                  className="flex-1"
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={(e) =>
                    handleLineEdit(index, e.currentTarget.textContent || "")
                  }
                >
                  {line.type === "added" && "+ "}
                  {line.type === "removed" && "- "}
                  {line.type === "unchanged" && "  "}
                  {line.value}
                </span>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export default InlineDiff;
